<template>
    <div>
        <row>
            <column v-if="!hiddenFields.user_id" cols="12">
                <lazy-form-user-select v-model="data.user_id"
                                  :name="`${validationPrefix}user_id`"
                                       :color="event.color"
                                  :label="$t('action.select_user')"
                                  :help="$t('help.participation.user_id')"
                                  :unselect="$t('action.register_other')"
                                  :data="userSuggestRequestData"
                                  group-prop="fetch_reason"
                                  :disabled="disabled"
                                  clearable
                                  :initial-option="data.user"
                                  @select="setUserAsParticipant"/>
            </column>

            <column v-if="!hiddenFields.email" cols="12">
                <form-input v-model="data.email"
                            :name="`${validationPrefix}email`"
                            :label="$t('label.email')"
                            :help="$t('help.participation.email')"
                            type="email"
                            :rules="{required: requiredFields.email, min: 4, max: 190}"
                            :disabled="disabled"/>
            </column>

            <column v-if="!hiddenFields.first_name" cols="12" lg="6">
                <form-input v-model="data.first_name"
                            :name="`${validationPrefix}first_name`"
                            :rules="{required: true, min: 2, max: 50}"
                            :disabled="disabled"
                            :label="$t('label.first_name')"/>
            </column>

            <column v-if="!hiddenFields.last_name" cols="12" lg="6">
                <form-input v-model="data.last_name"
                            :name="`${validationPrefix}last_name`"
                            :disabled="disabled"
                            :label="$t('label.last_name')"
                            :rules="{required: true, min: 2, max: 50}"/>
            </column>

            <column v-if="!hiddenFields.gender" cols="12" :lg="!hiddenFields.birthday ? 6 : 12">
                <lazy-form-gender-select v-model="data.gender"
                                         :color="event.color"
                                         :name="`${validationPrefix}gender`"
                                         :label="$t('label.gender')"
                                    :rules="{required: requiredFields.gender}"
                                    :disabled="disabled"/>
            </column>

            <column v-if="!hiddenFields.birthday" cols="12" :lg="!hiddenFields.gender ? 6 : 12">
				<form-native-date-input v-model="data.birthday"
										:disabled="disabled"
										:name="`${validationPrefix}birthday`"
										:label="$t('label.birthday')"
										:rules="{required: requiredFields.birthday, max: $date().format('YYYY-MM-DD'), min: $date('1900-01-01').format('YYYY-MM-DD')}">
					<template #suffix>
	<span class="px-8 text-subtext-color">

                        {{ resolvedAgeClass ? resolvedAgeClass.name : '' }}

    </span>
					</template>
				</form-native-date-input>
            </column>

            <column v-if="!hiddenFields.team_id" cols="12">
                <lazy-form-team-select v-model="data.team_id"
                                  :disabled="disabled"
                                  :event-id="event.id"
                                       :name="`${validationPrefix}team_id`"
                                       :color="event.color"
                                  :participation-class-id="data.participation_class_id"
                                  allow-create
                                  clearable
                                       :force-private="requiredFields.team_id"
                                  :rules="{required: requiredFields.team_id}"
                                  :help="requiredFields.team_id ? $t('text.participation.choose_participation_team') : $t('text.participation.choose_participation_team_optional')"
                                  :label="participationClass.team_settings?.label ?? $t('label.squad')"/>
            </column>

            <column v-if="!hiddenFields.club_id" cols="12">
                <lazy-form-club-select v-model="data.club_id"
                                       :color="event.color"
                                  :help="$t('help.participation.club_id')"
                                       :name="`${validationPrefix}club_id`"
                                       :disabled="disabled"
                                  allow-create
                                  clearable
									   :extended="[3288, 3287, 3590, 3664, 4240].includes(event.id)"
                                  :rules="{required: requiredFields.club}"
                                  :label="[3288, 3287, 3590, 3664, 4240].includes(event.id) ? 'Firma / Schule / Verein' : $t('label.club_company')"/>
            </column>

        </row>

        <template v-if="!hiddenFields.address || !hiddenFields.phone">
            <p class="subheading mb-16">{{ $t('label.contact_data') }}</p>

            <contact-form-inputs :data="data"
                                 :phone="!hiddenFields.phone"
                                 :emergency-phone="!hiddenFields.phone"
                                 :street="!hiddenFields.street"
                                 :slim="true"
								 :color="event.color"
                                 :disabled="disabled"
                                 :validation-prefix="validationPrefix"
                                 disable-placeholders
                                 :required="requiredFields.address || requiredFields.street"
                                 :no-address="hiddenFields.address"
                                 :street-required="requiredFields.street"
                                 :phone-required="requiredFields.phone" help-prefix="help.participation"/>
        </template>

        <p class="subheading mb-16">{{ $t('label.advanced_information') }}</p>

        <row>

            <column v-if="!hiddenFields.license_number" cols="12">
                <form-input v-model="data.license_number"
                            :disabled="disabled"
                            :name="`${validationPrefix}license_number`"
                            :label="licenseNumberLabel"
                            :help="$t('help.participation.license_number')"
                            :rules="{required: requiredFields.license_number, max: 30}">
                </form-input>
            </column>

            <column v-if="participationClass && participationClass.inputs && participationClass.inputs.length && data.inputs" cols="12">
                <lazy-custom-form-inputs :inputs="participationClass.inputs"
                                         :data="data.inputs"
                                         :color="event.color" :disabled="disabled"/>
            </column>

            <column cols="12">
                <form-textarea :label="$t('input.participation.participant_info')"
                               :name="`${validationPrefix}participant_info`"
                               v-model="data.participant_info" :help="$t('help.participation.participant_info')" :rows="3"/>
            </column>

        </row>

		<div v-if="!hiddenFields.private" class="mb-12">
			<p class="subheading mb-16">{{ $t('label.privacy') }}</p>

			<form-switch name="private" v-model="data.private" :label="$t('input.participation.private')"
						 :disabled="disabled"
						 :help="$t('help.participation.private')"/>
		</div>

        <template v-if="participationClass && participationClass.products && participationClass.products.length && data.productPurchases">
            <p class="subheading">{{ $t('label.participation_products') }}</p>
            <p class="subheading-description mb-24">{{ $t('help.participation.bookable_participation_products') }}</p>

            <event-product-purchase-form-inputs :data="data.productPurchases" :participation="data" :products="participationClass.products" :event="event"
                                                :item-list-id="`event-participation-form-${event.id}`"
                                                :item-list-name="`Event Participation Form: ${event.display_name}`"
                                                with-required-products/>

        </template>

        <template v-if="!withoutValidation && validationErrorMessage">
            <validation-input :model-value="!validationErrorMessage" :error="`${data.first_name} ${data.last_name}: ${validationErrorMessage}`"/>
            <template v-if="validationErrorMessage">
                <lazy-alert
                    v-if="data.user_id && $auth.id === data.user_id && participationClass && participationClass._auth_relations && participationClass._auth_relations.is_participant"
                    type="warning" class="mt-20" :content="validationErrorMessage"
                    :button="$t('action.to_participation_page')"
                    :button-icon="false"
                    :to="$web('events.participations.show', [event._key, participationClass._auth_relations.participation_id])"/>
                <lazy-alert v-else type="warning" class="mt-20" :content="validationErrorMessage"/>
            </template>
        </template>

    </div>

</template>

<script>
import {ParticipationInputState} from "@spoferan/spoferan-ts-core";
import {ParticipationClassTeamMode} from "@spoferan/spoferan-ts-core";
import {FormInputsMixin} from "@spoferan/nuxt-spoferan/mixins";;
import {getSportTypeLicenseNumberLangKey} from "@spoferan/spoferan-ts-core";
import ParticipationRegistrationType from "@/models/ParticipationRegistrationType";

export default {
    name: 'ParticipationFormInputs',

    mixins: [FormInputsMixin],

	emits: ['active-price-changed'],

    props: {

        // The event to create the participation for
        event: {
            type: Object,
            required: true
        },

        // The participation class to register the participation for
        participationClass: {
            type: Object,
            required: true
        },

        // The ParticipationRegistrationType under which the participation gets created for
        // Dependent on the type, only specific inputs are being displayed
        type: {
            type: String,
            default: null
        },

        // The prefix to prepend on the input names, so validation errors on the backend can be assigned to the correct input field
        validationPrefix: {
            type: String,
            default: ''
        },

        withoutValidation: {
            type: Boolean,
            default: false
        },
    },

    data() {
        return {

            // The fields that shall be hidden due to the selected participation class and registration type
            hiddenFields: {},

            // The fields that might be required dependent on the selected participation class
            requiredFields: {},

            // The mail that has been checked last for an existent profile
            // Required to not ask the user twice about the same email address
            lastCheckedEmail: null,

            // The age class resolved for the participation data or false if not resolved yet
            resolvedAgeClass: false,

            // The data to send to the API when fetching the user suggests
            userSuggestRequestData: {
                limit: 500,
                type: 'participation'
            }
        }
    },

    computed: {

        // An error message why the participation data can currently not be submitted that is not related to a single input.
        validationErrorMessage() {
            // Check if participationClass is selected
            if (!this.participationClass) {
                return this.$t('restriction.participation_class.failed.missing')
            }

            // Check if participation dates are set
            if (!this.participationClass.register_start_date || !this.participationClass.register_end_date) {
                return this.$t('restriction.participation_class.failed.missing_registration_window')
            }

            // Check if before participation window
            if (this.$date().isBefore(this.participationClass.register_start_date)) {
                return this.$t('restriction.participation_class.failed.before_registration_window', {date: this.$date(this.participationClass.register_start_date).calendar()})
            }

            // Check if after participation window
            if (this.$date().isAfter(this.participationClass.register_end_date)) {
                return this.$t('restriction.participation_class.failed.after_registration_window', {date: this.$date(this.participationClass.register_end_date).calendar()})
            }

            // Check if already a participant
            if (this.participationClass._auth_relations && this.participationClass._auth_relations.id === this.data.user_id && this.participationClass._auth_relations.is_participant) {
                return this.$t('restriction.participation_class.failed.already_participant')
            }

            // Check limit restriction
            if (this.participationClass.restrictions.limit && this.participationClass.participants_count !== null && this.participationClass.participants_count >= this.participationClass.restrictions.limit) {
                return this.$t('restriction.participation_class.failed.limit', {limit: this.participationClass.restrictions.limit})
            }

            // Check event limit restriction
			// TODO: This is currently not checked as we do not have the participants count and restrictions available -> fix with explicit view EP
            // if (this.restrictions && this.restrictions.limit && this.event.participants_count !== null && this.event.participants_count >= this.restrictions.limit) {
            //     return this.$t('restriction.event.failed.limit', {limit: this.restrictions.limit})
            // }

            // Check gender restriction
            if (this.participationClass.restrictions.gender && this.data.gender && this.data.gender !== this.participationClass.restrictions.gender) {
                return this.$t('restriction.participation_class.failed.gender')
            }

            // Check age restriction
            if (this.data.birthday) {
                if (this.participationClass.restrictions.min_age) {
                    const compareDate = this.participationClass.restrictions.min_age_compare === 'event' ? this.$date(this.participationClass.start_date) : this.$date(this.participationClass.start_date).month(12).day(31);
                    if (compareDate.diff(this.data.birthday, 'years') < this.participationClass.restrictions.min_age) {
                        return this.participationClass.restrictions.min_age_compare === 'event' ? this.$t('restriction.participation_class.min_age_event', {age: this.participationClass.restrictions.min_age}) : this.$t('restriction.participation_class.min_age_year', {age: this.participationClass.restrictions.min_age})
                    }
                }
                if (this.participationClass.restrictions.max_age) {
                    const compareDate = this.participationClass.restrictions.max_age_compare === 'event' ? this.$date(this.participationClass.start_date) : this.$date(this.participationClass.start_date).month(12).day(31);
                    if (compareDate.diff(this.data.birthday, 'years') > this.participationClass.restrictions.max_age) {
                        return this.participationClass.restrictions.max_age_compare === 'event' ? this.$t('restriction.participation_class.max_age_event', {age: this.participationClass.restrictions.max_age}) : this.$t('restriction.participation_class.max_age_year', {age: this.participationClass.restrictions.max_age})
                    }
                }
            }

            // Check active price restriction
            if (!this.data.activePrice && this.participationClass?.team_settings?.fee_charge_mode !== 'team') {
                return this.$t('restriction.participation_class.failed.active_price', {age: this.$date(this.data.birthday).age()})
            }

            // Check location restriction
            const locationRestriction = this.failsLocationRestriction(this.participationClass, this.data)
            if (locationRestriction) {
                return locationRestriction
            }

            return null
        },

        licenseNumberLabel() {
            const licenseNumberLangKey = getSportTypeLicenseNumberLangKey(this.participationClass ? this.participationClass.sport_type_id : null);

            return this.$t(`input.participation.license_number.${licenseNumberLangKey}`);
        },

    },

    watch: {

        'data.participation_class_id'(newId, oldId) {
            this.adaptDataForParticipationClass(oldId)
        },

        'data.birthday'() {
            this.refreshActivePriceForParticipation();
            this.resolveAgeClass();
        },

        'data.gender'() {
            this.resolveAgeClass();
        },

        type: {
            handler: function (type) {
                // We initialize the form with the logged user's data if specified and not already set (would overwrite existing modifications)
                if (type === ParticipationRegistrationType.Myself && this.$auth.user && this.data.user_id !== this.$auth.user.id) {
                    this.setUserAsParticipant(this.$auth.user)
                }
                this.ensureValidDataExists();
            },
            immediate: true
        }
    },

    created() {
        this.adaptDataForParticipationClass();
    },

    methods: {

        /**
         * Checks if the specified participation can participate in the participation class when just considering
         * the location restriction of the participation class. Gets a user readable text if the check fails.
         * BlackListEntry: This requires to check if a user is authenticated before checking with this method, i.e., not
         * authenticated visitors will pass this check.
         *
         * @returns {boolean|string}
         */
        failsLocationRestriction(participationClass, participation) {
            if (participationClass.restrictions && participationClass.restrictions.origin && participationClass.restrictions.origin.country_code) {
                if (!participation || participationClass.restrictions.origin.country_code !== participation.country_code) {
                    return this.$t('restriction.participation_class.failed.country_code', {country: participationClass.restrictions.origin.country_code.toUpperCase()})
                }

                if (!participation || participationClass.restrictions.origin.state_code && participationClass.restrictions.origin.state_code !== participation.state_code) {
                    return this.$t('restriction.participation_class.failed.state_code', {
                        country: participationClass.restrictions.origin.country_code.toUpperCase(),
                        state: participationClass.restrictions.origin.state_code.toUpperCase()
                    })
                }

                if (!participation || participationClass.restrictions.origin.postcode && participationClass.restrictions.origin.postcode !== participation.postcode) {
                    return this.$t('restriction.participation_class.failed.postcode', {
                        country: participationClass.restrictions.origin.country_code.toUpperCase(),
                        state: participationClass.restrictions.origin.state_code.toUpperCase(),
                        postcode: participationClass.restrictions.origin.postcode
                    })
                }
            }

            return false
        },

        /**
         * Sets the specified user as the participant.
         *
         * @param {object|null} user
         */
        setUserAsParticipant(user) {
            this.data['user_id'] = user ? user.id : null
            this.data['first_name'] = user ? user.first_name : null
            this.data['last_name'] = user ? user.last_name : null
            this.data['gender'] = user ? user.gender : null
            this.data['birthday'] = user ? user.birthday : null
            this.data['email'] = user ? user.email : null
            this.data['country_code'] = user ? user.country_code : null
            this.data['state_code'] = user ? user.state_code : null
            this.data['postcode'] = user ? user.postcode : null
            this.data['city'] = user ? user.city : null
            this.data['street'] = user ? user.street : null
            this.data['phone'] = user ? user.phone : null
            this.data['emergency_phone'] = user ? user.emergency_phone : null

            // Pre-fill the user's main club
            if (!this.hiddenFields.club_id) {
                this.data['club_id'] = user ? user.club_id : null
            }

            // Set the full user to restore it as selectable user when component gets re-created
            this.data['user'] = user

            // Refresh the hidden fields after all components are updated, e.g., the user select is closed, to prevent render issues
            this.$nextTick(function () {
                // Must be set on the data to be persistent on component destroys
                this.data['_hide_address'] = user && (!this.$auth.user || this.$auth.user.id !== user.id) && !!user.has_full_address // Let the authenticated user edit his contact data
                this.data['_hide_phone'] = user && (!this.$auth.user || this.$auth.user.id !== user.id) && !!user.has_phone // Let the authenticated user edit his contact data
                this.data['_hide_birthday'] = user && !!user.birthday
                this.data['_hide_gender'] = user && !!user.gender
                this.refreshInputStates();
            });
        },

        /**
         * Updates the currently active price for the participation in its participation class.
         */
        refreshActivePriceForParticipation() {
            this.data['activePrice'] = this.findActivePrice()
			this.$emit('active-price-changed', this.data.activePrice)
		},

        /**
         * Handles the event when a new participation class was selected.
         */
        adaptDataForParticipationClass(oldParticipationClassId = null) {

            // Unset the participation data that is not relevant anymore due to the change
            if (this.participationClass && this.participationClass.settings) {
                if (this.participationClass.settings.gender_input === ParticipationInputState.Disabled) {
                    delete this.data['gender'];
                }
                if (this.participationClass.settings.birthday_input === ParticipationInputState.Disabled) {
                    delete this.data['birthday'];
                }
                if (this.participationClass.settings.address_input === ParticipationInputState.Disabled) {
                    delete this.data['country_code'];
                    delete this.data['state_code'];
                    delete this.data['postcode'];
                    delete this.data['city'];
                    delete this.data['street'];
                }
                if (this.participationClass.settings.street_input === ParticipationInputState.Disabled) {
                    delete this.data['street'];
                }
                if (this.participationClass.settings.phone_input === ParticipationInputState.Disabled) {
                    delete this.data['phone'];
                    delete this.data['emergency_phone'];
                }
                if (this.participationClass.settings.license_number_input === ParticipationInputState.Disabled) {
                    delete this.data['license_number'];
                }
                if (!this.participationClass.settings.privacy_enabled) {
                    delete this.data['private'];
                }

                // Teams always differ between participation classes
                if (oldParticipationClassId) {
                    delete this.data['team_id'];
                }
            }

            this.ensureValidDataExists();
        },

        ensureValidDataExists() {
			const inputsData = {};
			if (this.participationClass) {
				this.participationClass.inputs.forEach(input => {
					const existingInputData = this.data.inputs ? (this.data.inputs[input.id] ?? {}) : {};
					inputsData[input.id] = {
						id: input.id,
						value: existingInputData.value ?? input.default_value,
						label: input.label, // Just for nice output
						type: input.type // For value parsing, especially for booleans
					};
				})
			}
			this.data['inputs'] = inputsData

			// Make sure the productPurchases object exists
			if (!this.data.productPurchases) {
				this.data['productPurchases'] = {}
			} else {
				// We remove all products that do not exist for the current participation class
				if (Object.keys(this.data.productPurchases).length) {
					Object.keys(this.data.productPurchases).forEach(productId => {
						// Input ids are strings when used as object key
						const participationClassProduct = this.participationClass.products.find(product => parseInt(product.id) === parseInt(productId));
						if (!participationClassProduct) {
							delete this.data.productPurchases[productId]
						}
					})
				}
			}

			this.refreshActivePriceForParticipation()

            this.refreshInputStates();
        },

        /**
         * Refreshes the visibility and validation rules of the form's input fields.
         */
        refreshInputStates() {

            // Set the hidden fields according to the participation class
            // TODO: Change to visibleFields like in resultastic as it's easier to read
            this.hiddenFields = {
                user_id: !this.$auth.loggedIn || this.type === ParticipationRegistrationType.Myself,
                email: !!this.data.user_id, // We always display the email input regardless of the participation class input settings
                first_name: !!this.data.user_id, // Name updates must occur via organizer
                last_name: !!this.data.user_id, // Name updates must occur via organizer
                gender: this.data._hide_gender || !this.participationClass || (this.participationClass.settings && this.participationClass.settings.gender_input === ParticipationInputState.Disabled && !this.participationClass.age_classes_exist),
                birthday: this.data._hide_birthday || !this.participationClass || (this.participationClass.settings && this.participationClass.settings.birthday_input === ParticipationInputState.Disabled && !this.participationClass.age_classes_exist),
                team_id: this.type === ParticipationRegistrationType.Team || !this.participationClass || (this.participationClass.settings && this.participationClass.settings.team_mode === ParticipationClassTeamMode.Disabled),
                club_id: this.type === ParticipationRegistrationType.Club || !this.participationClass || (this.participationClass.settings && this.participationClass.settings.club_input === ParticipationInputState.Disabled),
                address: this.data._hide_address || !this.participationClass || (this.participationClass.settings && this.participationClass.settings.address_input === ParticipationClassTeamMode.Disabled && this.participationClass.settings.street_input === ParticipationClassTeamMode.Disabled),
                street: !this.participationClass || (this.participationClass.settings && this.participationClass.settings.street_input === ParticipationClassTeamMode.Disabled),
                phone: this.data._hide_phone || !this.participationClass || (this.participationClass.settings && this.participationClass.settings.phone_input === ParticipationClassTeamMode.Disabled),
                license_number: !this.participationClass || (this.participationClass.settings && this.participationClass.settings.license_number_input === ParticipationClassTeamMode.Disabled),
                private: !this.participationClass || (this.participationClass.settings && !this.participationClass.settings.privacy_enabled)
            }

            // Set the required fields according to the participation class
            this.requiredFields = {
                email: this.type === ParticipationRegistrationType.Myself || (this.participationClass && (this.participationClass.settings && this.participationClass.settings.email_input === ParticipationInputState.Required)),
                gender: this.participationClass && ((this.participationClass.settings && this.participationClass.settings.gender_input === ParticipationInputState.Required) || this.participationClass.age_classes_exist),
                birthday: this.participationClass && ((this.participationClass.settings && this.participationClass.settings.birthday_input === ParticipationInputState.Required) || this.participationClass.age_classes_exist),
                address: this.participationClass && (this.participationClass.settings && this.participationClass.settings.address_input === ParticipationInputState.Required),
                street: this.participationClass && (this.participationClass.settings && this.participationClass.settings.street_input === ParticipationInputState.Required),
                phone: this.participationClass && (this.participationClass.settings && this.participationClass.settings.phone_input === ParticipationInputState.Required),
                club: this.participationClass && (this.participationClass.settings && this.participationClass.settings.club_input === ParticipationInputState.Required),
                license_number: this.participationClass && (this.participationClass.settings && this.participationClass.settings.license_number_input === ParticipationInputState.Required),
                team_id: this.participationClass?.team_settings?.required
            }
        },

        /**
         * Gets the active price for the entered participation data and the selected participation class.
         */
        findActivePrice() {
            // Team participation fees are set on the ParticipationGroupFormInputs component
            if (!this.participationClass || this.participationClass?.team_settings?.fee_charge_mode === 'team') {
                return null;
            }

            const compareDate = this.participationClass.start_date && this.participationClass.start_date ? this.$date(this.participationClass.start_date) : this.$date()
            const prices = this.participationClass.prices
            if (prices && prices.length) {
                const activePrices = prices.filter(price => {
                    return price.is_active && !price.for_additional && ((!this.data.birthday) || ((!price.min_age || compareDate.diff(this.data.birthday, 'years') >= price.min_age) && (!price.max_age || compareDate.diff(this.data.birthday, 'years') <= price.max_age)))
                })

                if (activePrices && activePrices.length) {

                    // The lowest active price will be taken by default
                    return activePrices.sort((a, b) => a.cents < b.cents ? -1 : 1)[0]
                }
            }

            return null;
        },

        resolveAgeClass() {
            if (!this.data.participation_class_id || !this.data.birthday || (this.requiredFields.gender && !this.data.gender)) {
                this.resolvedAgeClass = false;
                return;
            }

            this.$apiFetch(this.$api('events.participation_classes.age_classes.resolve', [this.event._key, this.data.participation_class_id]), {
                params: {
                    birthday: this.data.birthday,
                    gender: this.data.gender,
                }
            }).then((response) => {
                this.resolvedAgeClass = response.data;
            }).catch(error => {
                console.error(error);
                this.resolvedAgeClass = false;
            })
        },

    }

}
</script>